"use client";

import {
    Container,
    Typography,
    Breadcrumb,
} from '@/components/design-system';
import { ArrowRight, Sparkles } from 'lucide-react';
import { ICategory } from '@/constants/categories';
import GeneratorCard from '@/components/partials/GeneratorCard';
import { useRouteBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useLocale, useTranslations } from 'next-intl';
export interface CategoryPageProps {
    category: ICategory;
}

export default function CategoryPage({ category }: CategoryPageProps) {
    const locale = useLocale();
    const t = useTranslations('categoryPage');
    const { breadcrumbs } = useRouteBreadcrumbs([
        { label: category.name[locale], isActive: true }
    ], { includeHome: false });

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-3xl" />
                
                <Container className="relative">
                    <div className="py-4">
                        {/* Breadcrumb Navigation */}
                        <div className="mb-8 animate-in fade-in-0 duration-1000 delay-100">
                            <Breadcrumb items={breadcrumbs} className="justify-center" />
                        </div>
                        
                        <div className="max-w-4xl mx-auto text-center space-y-8">
                            {/* Category Icon and Title */}
                            <div className="space-y-6 animate-in fade-in-0 duration-1000 delay-200">
                                {/* <div className="flex justify-center">
                                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center border border-primary/20">
                                        {React.cloneElement(category.icon, { className: 'h-8 w-8 text-primary' } as any)}
                                    </div>
                                </div> */}
                                
                                <Typography 
                                    variant="h1" 
                                    className="bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent leading-tight"
                                >
                                    {category.name[locale]}
                                </Typography>
                                
                                <Typography variant="lead" className="max-w-2xl mx-auto text-muted-foreground leading-relaxed">
                                    {category.description[locale]}
                                </Typography>
                            </div>
                            
                            {/* Stats or Features */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto animate-in fade-in-0 duration-1000 delay-700">
                                <div className="flex items-center gap-3 justify-center">
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                        <Sparkles className="h-4 w-4 text-primary" />
                                    </div>
                                    <Typography variant="muted">{t('hero.highQuality')}</Typography>
                                </div>
                                <div className="flex items-center gap-3 justify-center">
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                        <ArrowRight className="h-4 w-4 text-primary" />
                                    </div>
                                    <Typography variant="muted">{t('hero.instantGeneration')}</Typography>
                                </div>
                                <div className="flex items-center gap-3 justify-center">
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                        <span className="text-primary font-bold text-sm">{category.generators.length}</span>
                                    </div>
                                    <Typography variant="muted">{t('hero.generatorsAvailable')}</Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Generators Listing Section */}
            <section className="py-20">
                <Container>
                    <div className="max-w-6xl mx-auto">
                        {/* Section Header */}
                        <div className="text-center mb-16">
                            <Typography variant="h2" className="mb-4">
                                {t('generators.title')}
                            </Typography>
                            <Typography variant="muted" className="text-lg">
                                {t('generators.description', { categoryName: category.name[locale].toLowerCase() })}
                            </Typography>
                        </div>

                        {/* Generators Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {category.generators.map((generator) => (
                                <GeneratorCard key={generator.id} generator={generator} />
                            ))}
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
}
